<template>
    <div class="flex flex-col equipment-planned-utilization-modal">
        <div class="flex justify-start mb-4">
            <CoreSelectButton
                v-model="specificity"
                :options="specificityOptions"
                class="toggle-monthly-inputs core-select-button--full-width"
            />
        </div>
        <DataTable
            v-model:expandedRowGroups="expandedRowGroups"
            :value="utilizationTableData"
            expandableRowGroups
            rowGroupMode="subheader"
            groupRowsBy="year"
            scrollable
            scrollHeight="600px"
            :rowClass="getRowClass"
        >
            <template #groupheader="slotProps">
                <div
                    class="table-row-group-header"
                    :class="{
                        'table-row-group-header--expanded':
                            expandedRowGroups && expandedRowGroups.length > 0,
                    }"
                >
                    <span class="text-bold table-row-group-header__label">
                        {{ $t('§caf.year') }} {{ slotProps.data.year + 1 }}
                        <span class="text-dark-muted table-row-group-header__note">
                            ({{ $t('§caf.sum') }})
                        </span>
                    </span>
                    <CoreNumberInput
                        class="utilization_input utilization_input--yearly"
                        :model-value="getYearInputValue(slotProps.data.year)"
                        :name="`year_utilization_${slotProps.data.year}`"
                        :maxFractionDigits="YEARLY_DECIMALS"
                        :disabled="disabledInputs"
                        :customNotificationMessage="getYearValidationMessage(slotProps.data.year)"
                        @update:modelValue="
                            handleUpdateYearUtilization(slotProps.data.year, $event)
                        "
                        @blur="settleYearUtilization(slotProps.data.year)"
                        @keydown.enter="handleEnterKey"
                    />
                </div>
            </template>
            <Column field="month" :header="$t('§caf.month')">
                <template #body="{ data }">
                    <div class="pl-3 flex items-center gap-4 month-label">
                        <span>
                            <CoreIcon
                                name="arrow-right-bottom"
                                size="22"
                                fillColor="var(--color-neutral)"
                            />
                        </span>
                        <span>{{ $t('§caf.month') }} {{ data.month }}</span>
                    </div>
                </template></Column
            >
            <Column
                field="utilization"
                :bodyStyle="{ width: `200px` }"
                headerClass="utilization-header"
            >
                <template #header="slotProps">
                    {{ $t('§caf.expected_utilization') }}
                </template>
                <template #body="{ data }">
                    <CoreNumberInput
                        class="utilization_input utilization_input--monthly"
                        :model-value="data.utilization"
                        :name="`utilization_${data.year}_${data.month}`"
                        :maxFractionDigits="MONTHLY_DECIMALS"
                        :disabled="disabledInputs"
                        @update:modelValue="handleUpdateUtilization(data, $event)"
                        @keydown.enter="handleEnterKey"
                    />
                </template>
            </Column>
        </DataTable>

        <div class="flex justify-start gap-2 mt-4">
            <CoreButton
                v-if="!disabledInputs"
                label="§ui_button.add_utilization"
                icon="pi-check"
                :disabled="isAddUtilizationDisabled"
                @click="handleSave"
            />
        </div>
    </div>
</template>

<script>
import { debounce } from 'lodash'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { useModalStore } from '@/stores/modal'
import { useCafStore } from '@/stores/caf'
import CoreIcon from '@/components/core/CoreIcon.vue'
import CoreButton from '@/components/core/CoreButton.vue'
import CoreNumberInput from '@/components/core/CoreNumberInput.vue'
import CoreSelectButton from '@/components/core/CoreSelectButton.vue'
import {
    PRODUCT_TYPE_NAMES,
    PLANNED_UTILIZATION_MONTHLY_DECIMALS,
    PLANNED_UTILIZATION_YEARLY_DECIMALS,
} from '@/data/constants'

export default {
    name: 'EquipmentPlannedUtilizationModal',

    components: {
        DataTable,
        Column,
        CoreButton,
        CoreIcon,
        CoreNumberInput,
        CoreSelectButton,
    },

    data() {
        return {
            modalStore: useModalStore(),
            cafStore: useCafStore(),

            utilizationTableData: [],
            specificity: 'monthly',
            expandedRowGroups: null,
            disabledInputs: false,
            debouncedYearUpdates: {},
            yearInputDrafts: {},

            MONTHLY_DECIMALS: PLANNED_UTILIZATION_MONTHLY_DECIMALS,
            YEARLY_DECIMALS: PLANNED_UTILIZATION_YEARLY_DECIMALS,
        }
    },

    computed: {
        specificityOptions() {
            return [
                { value: 'monthly', label: this.$t('§caf.monthly') },
                { value: 'yearly', label: this.$t('§caf.yearly') },
            ]
        },

        isUnitTypeSelected() {
            return this.cafStore.sharedInputs.unit_type !== null
        },

        selectedUnitTypeMaxUsage() {
            if (this.isUnitTypeSelected) {
                return this.cafStore[PRODUCT_TYPE_NAMES.PPU].unit_type_max_usage
            }

            return null
        },

        doesPlannedUtilizationExceedMax() {
            if (!this.isUnitTypeSelected || this.selectedUnitTypeMaxUsage === null) {
                return false
            }

            const uniqueYears = [...new Set(this.utilizationTableData.map((item) => item.year))]
            return uniqueYears.some(
                (year) => this.getYearUtilizationSum(year) > this.selectedUnitTypeMaxUsage
            )
        },

        isAnyMonthUtilizationInvalid() {
            return this.utilizationTableData.some((item) => (item.utilization || 0) <= 0)
        },

        isAddUtilizationDisabled() {
            return this.doesPlannedUtilizationExceedMax || this.isAnyMonthUtilizationInvalid
        },

        yearUtilizationExceedsMax() {
            if (!this.isUnitTypeSelected || this.selectedUnitTypeMaxUsage === null) {
                return {}
            }

            const exceedsMap = {}
            const uniqueYears = [...new Set(this.utilizationTableData.map((item) => item.year))]

            uniqueYears.forEach((year) => {
                const yearSum = this.getYearUtilizationSum(year)
                exceedsMap[year] = yearSum > this.selectedUnitTypeMaxUsage
            })

            return exceedsMap
        },
    },

    watch: {
        specificity(newValue) {
            if (newValue === 'monthly') {
                const uniqueYears = [...new Set(this.utilizationTableData.map((item) => item.year))]
                this.expandedRowGroups = uniqueYears
            } else {
                this.expandedRowGroups = null
            }
        },
    },

    created() {
        this.utilizationTableData = this.modalStore.data?.tableData || []
        this.disabledInputs = this.modalStore.data?.disabledInputs || false
    },

    mounted() {
        // performance issue expand only the first year for faster initial render
        this.expandedRowGroups = [0]
        // later expand all year groups
        setTimeout(() => {
            if (this.specificity === 'monthly') {
                const uniqueYears = [...new Set(this.utilizationTableData.map((item) => item.year))]
                this.expandedRowGroups = uniqueYears
            }
        }, 500)
    },

    beforeUnmount() {
        Object.values(this.debouncedYearUpdates).forEach((debouncedFn) => {
            debouncedFn.cancel()
        })
    },

    methods: {
        handleUpdateUtilization(data, newValue) {
            const index = this.utilizationTableData.findIndex(
                (item) => item.year === data.year && item.month === data.month
            )
            if (index !== -1) {
                this.utilizationTableData[index].utilization = newValue
            }
        },

        getYearUtilizationSum(year) {
            const yearData = this.utilizationTableData.filter((item) => item.year === year)
            if (yearData.length === 0) return 0
            const sum = yearData.reduce((acc, item) => acc + (item.utilization || 0), 0)
            const factor = 10 ** PLANNED_UTILIZATION_YEARLY_DECIMALS
            return Math.round(sum * factor) / factor
        },

        getYearInputValue(year) {
            return this.yearInputDrafts[year] ?? this.getYearUtilizationSum(year)
        },

        settleYearUtilization(year) {
            this.debouncedYearUpdates[year]?.flush()
            delete this.yearInputDrafts[year]
        },

        handleUpdateYearUtilization(year, newValue) {
            this.yearInputDrafts[year] = newValue

            if (!this.debouncedYearUpdates[year]) {
                this.debouncedYearUpdates[year] = debounce((yearVal, value) => {
                    const monthlyValue = value / 12
                    this.utilizationTableData.forEach((item, index) => {
                        if (item.year === yearVal) {
                            this.utilizationTableData[index].utilization = monthlyValue
                        }
                    })
                }, 600)
            }

            this.debouncedYearUpdates[year](year, newValue)
        },

        handleSave() {
            const utilizationArray = this.utilizationTableData.map((item) => item.utilization || 0)
            this.cafStore[PRODUCT_TYPE_NAMES.PPU].expected_utilization_by_month = utilizationArray
            this.cafStore[PRODUCT_TYPE_NAMES.PPU].expected_utilization = null
            this.modalStore.setClose()
        },

        getRowClass(data) {
            if (data.year % 2 === 0) {
                return 'row-highlight'
            }
            return ''
        },

        getYearValidationMessage(year) {
            if (!this.isUnitTypeSelected || this.selectedUnitTypeMaxUsage === null) {
                return null
            }

            const yearSum = this.getYearUtilizationSum(year)
            if (yearSum > this.selectedUnitTypeMaxUsage) {
                return {
                    severity: 'error',
                    message: `${this.$t('§caf.utilization_exceeds_maximum_allowed')} (${
                        this.selectedUnitTypeMaxUsage
                    })`,
                }
            }

            return null
        },

        handleEnterKey(event) {
            event.preventDefault()

            const inputs = Array.from(
                this.$el.querySelectorAll('input[type="text"]:not(:disabled)')
            )

            if (inputs.length === 0) return

            const currentInput = event.target
            const currentIndex = inputs.indexOf(currentInput)

            if (currentIndex === -1) return

            const nextIndex = (currentIndex + 1) % inputs.length
            inputs[nextIndex].focus()
            inputs[nextIndex].select()
        },
    },
}
</script>

<style scoped>
.equipment-planned-utilization-modal .toggle-monthly-inputs {
    margin: 1rem 0;
    width: 100%;
    display: flex;
}

.equipment-planned-utilization-modal
    .toggle-monthly-inputs.core-select-button.p-selectbutton
    :deep(> .p-togglebutton) {
    flex: 1;
}

.equipment-planned-utilization-modal :deep(.p-datatable .p-datatable-row-group-header) {
    position: relative !important;
    top: auto !important;
    z-index: inherit !important;
}

.equipment-planned-utilization-modal :deep(tr.p-datatable-row-group-header:nth-child(odd)) {
    background-color: var(--color-primary-100);
}

.equipment-planned-utilization-modal :deep(tr.row-highlight) {
    background-color: var(--color-primary-100);
}

.equipment-planned-utilization-modal :deep(td:has(.table-row-group-header)) {
    display: flex;
    position: relative;
    width: 150%;
}

.equipment-planned-utilization-modal
    :deep(td:has(.table-row-group-header.table-row-group-header--expanded)) {
    width: 165%;
}

.equipment-planned-utilization-modal :deep(td) {
    --p-datatable-body-cell-border-color: var(--color-neutral-500);
}

.equipment-planned-utilization-modal :deep(td button) {
    margin-right: 16px;
    margin-top: 28px;
}

.equipment-planned-utilization-modal :deep(.p-datatable-row-toggle-button) {
    background-color: var(--color-neutral-400);
    width: 24px;
    height: 24px;
}

.equipment-planned-utilization-modal :deep(.p-datatable-row-toggle-button > svg) {
    height: 14px !important;
    width: 14px !important;
    min-height: 14px !important;
    min-width: 14px !important;
    max-height: 14px !important;
    max-width: 14px !important;
}

.equipment-planned-utilization-modal :deep(td .table-row-group-header) {
    display: flex;
    justify-content: space-between;
    width: 100%;
}

.equipment-planned-utilization-modal :deep(td .table-row-group-header span) {
    display: flex;
    align-items: center;
}

.equipment-planned-utilization-modal :deep(.utilization_input) {
    width: 200px;
}

.equipment-planned-utilization-modal :deep(.utilization_input--yearly) {
    margin: 16px 0;
}

.equipment-planned-utilization-modal :deep(.utilization_input--monthly) {
    margin-left: auto;
}

.equipment-planned-utilization-modal :deep(.utilization-header) {
    display: flex;
    font-weight: bold;
    justify-content: flex-end;
}

.month-label {
    width: 200px;
}

.table-row-group-header__label {
    position: relative;
}

.table-row-group-header__note {
    position: absolute;
    left: 110%;
}
</style>
